import * as React from 'react';

import { MapEvent } from '~/config/map';
import isEqual from 'lodash/isEqual';
import WrappedMap from '.';
import { MapPostEventWrapperRef } from './MapPostEventWrapper';
import { MapProps } from './Map';

export interface MapMarker {
  lat: number;
  long: number;
  image?: string;
  batteryLevel?: number;
  insertedMarker?: boolean;
  selected?: boolean;
}

export interface IndoorMapMarker extends MapMarker {
  id: string;
  label?: string;
  type?: string;
  zoneUuid?: string;
  roomNumber?: string;
  subType?: string | null;
}

export type OnMarkerPressedEvent = { id: string };
export type OnFloorLoadedEvent = { id: number };
export type OnPlaneSelectedEvent = { id: number };
export type OnMarkerInsertEvent = {
  planeId: number;
  planeUuid: string;
  zoneUuid?: string;
  zoneName?: string;
  x: number;
  y: number;
  siteId: number;
};
export type GoToMarkerEvent = { id: string; lat: number; long: number };

type TProps = MapProps & {
  markers?: IndoorMapMarker[];
  goToDevice?: GoToMarkerEvent;
  onMarkerPressed?: (e: OnMarkerPressedEvent) => void;
  onFloorLoaded?: (e: OnFloorLoadedEvent) => void;
  onPlaneSelectedEvent?: (e: OnPlaneSelectedEvent) => void;
  onMarkerInsert?: (e: OnMarkerInsertEvent) => void;
};

const DynamicMap = React.forwardRef<MapPostEventWrapperRef, TProps>(
  (
    { height, markers, goToDevice, onMarkerPressed, onFloorLoaded, onPlaneSelectedEvent, onMarkerInsert, ...params },
    map: React.RefObject<MapPostEventWrapperRef>
  ) => {
    const [isFloorLoaded, setFloorLoaded] = React.useState(false);
    const prevParams = React.useRef(params);

    const handleFloorLoadedEvent = React.useCallback(
      (e: OnFloorLoadedEvent) => {
        setFloorLoaded(true);
        onFloorLoaded?.(e);
      },
      [onFloorLoaded]
    );

    React.useEffect(() => {
      if (!isEqual(prevParams.current, params)) {
        setFloorLoaded(false);
        prevParams.current = params;
        map?.current?.sendEvent(MapEvent.UpdateParams, params);
      }
    }, [params]);

    React.useEffect(() => {
      if (isFloorLoaded) {
        map?.current?.sendEvent(MapEvent.UpdateMarkers, markers ?? []);
      }
    }, [markers, isFloorLoaded]);

    React.useEffect(() => {
      if (!onMarkerPressed) return;
      map?.current?.listenToEvent(MapEvent.MarkerPressed, onMarkerPressed);
      return () => map.current?.removeEventListener(onMarkerPressed);
    }, [onMarkerPressed]);

    React.useEffect(() => {
      map?.current?.listenToEvent(MapEvent.FloorLoaded, handleFloorLoadedEvent);
      return () => map.current?.removeEventListener(handleFloorLoadedEvent);
    }, [handleFloorLoadedEvent]);

    React.useEffect(() => {
      if (!onPlaneSelectedEvent) return;
      map?.current?.listenToEvent(MapEvent.PlaneSelected, onPlaneSelectedEvent);
      return () => map.current?.removeEventListener(onPlaneSelectedEvent);
    }, [onPlaneSelectedEvent]);

    React.useEffect(() => {
      if (!onMarkerInsert) return;
      map?.current?.listenToEvent(MapEvent.InsertMarker, onMarkerInsert);
      return () => map.current?.removeEventListener(onMarkerInsert);
    }, [onMarkerInsert]);

    React.useEffect(() => {
      if (!goToDevice || !isFloorLoaded) return;
      map?.current?.sendEvent(MapEvent.GoToMarker, goToDevice);
    }, [goToDevice, isFloorLoaded]);

    const startParams = React.useMemo(() => params, []);
    return (
      <>
        <WrappedMap ref={map} floorSelect={false} height={height} {...startParams} />
      </>
    );
  }
);

export default DynamicMap;
